import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { formatCurrency } from '../utils/formatters';

const UnitBadge = ({ text = '단위: 백만원' }) => (
  <div className="text-xs text-gray-500 absolute right-4 top-3">{text}</div>
);

const periodLabel = (period) => {
  switch (period) {
    case 'year':
      return '년';
    case 'month':
      return '월';
    case 'day':
      return '일';
    default:
      return '월';
  }
};

// 매출 차트 컴포넌트
export const SalesChart = ({ data, className = "", period = 'month' }) => {
  const scaleToMillions = (v) => Math.round((v || 0) / 1_000_000);
  const formatYAxis = (v) => `${v.toLocaleString()}`; // already scaled
  const tooltipFormatter = (value) => [`${(value || 0).toLocaleString()} 백만원`, '매출'];
  const xLabel = periodLabel(period);
  // 데이터 복사해서 매출을 백만원 단위로 스케일링하여 Y축 표시는 백만원으로
  const scaledData = (data || []).map(d => ({ ...d, sales_million: scaleToMillions(d.sales) }));

  return (
    <div className={`bg-white p-4 rounded-lg shadow relative ${className}`}>
      <h3 className="text-lg font-semibold mb-4">매출 추이</h3>
      <UnitBadge />
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={scaledData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" label={{ value: xLabel, position: 'insideBottomRight', offset: -5 }} />
          <YAxis tickFormatter={formatYAxis} />
          <Tooltip
            formatter={tooltipFormatter}
            labelFormatter={(label) => `기간: ${label}`}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="sales_million"
            stroke="#3B82F6"
            strokeWidth={2}
            name="매출(백만원)"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// 계약 건수 차트 컴포넌트
export const ContractChart = ({ data, className = "", period = 'month' }) => {
  const xLabel = periodLabel(period);
  return (
    <div className={`bg-white p-4 rounded-lg shadow ${className}`}>
      <h3 className="text-lg font-semibold mb-4">계약 건수</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" label={{ value: xLabel, position: 'insideBottomRight', offset: -5 }} />
          <YAxis />
          <Tooltip
            formatter={(value) => [value, '건수']}
            labelFormatter={(label) => `기간: ${label}`}
          />
          <Legend />
          <Bar dataKey="contract_count" fill="#10B981" name="계약 건수" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// 묘지 형태별 통계 차트
export const TombTypeChart = ({ data, className = "" }) => {
  const scaleToMillions = (v) => Math.round((v || 0) / 1_000_000);
  const formatYAxis = (v) => `${v.toLocaleString()}`;
  const tooltipFormatter = (value, name, props) => {
    // props.dataKey 또는 name을 사용하여 dataKey 확인
    const dataKey = props?.dataKey || name;
    const isContractCount = dataKey === 'contract_count';
    return [
      isContractCount ? `${value}건` : `${(value || 0).toLocaleString()} 백만원`,
      isContractCount ? '계약 건수' : '총 매출'
    ];
  };
  const scaledData = (data || []).map(d => ({ ...d, total_sales_million: scaleToMillions(d.total_sales) }));

  return (
    <div className={`bg-white p-4 rounded-lg shadow relative ${className}`}>
      <h3 className="text-lg font-semibold mb-4">묘지 형태별 통계</h3>
      <UnitBadge />
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={scaledData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="tomb_type" />
          <YAxis tickFormatter={formatYAxis} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend />
          <Bar dataKey="contract_count" fill="#3B82F6" name="계약 건수" />
          <Bar dataKey="total_sales_million" fill="#10B981" name="총 매출(백만원)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// 지역별 통계 차트
export const RegionalChart = ({ data, className = "" }) => {
  const scaleToMillions = (v) => Math.round((v || 0) / 1_000_000);
  const formatYAxis = (v) => `${v.toLocaleString()}`;
  const tooltipFormatter = (value, name, props) => {
    // props.dataKey 또는 name을 사용하여 dataKey 확인
    const dataKey = props?.dataKey || name;
    const isContractCount = dataKey === 'contract_count';
    return [
      isContractCount ? `${value}건` : `${(value || 0).toLocaleString()} 백만원`,
      isContractCount ? '계약 건수' : '총 매출'
    ];
  };
  
  // 데이터 확인 및 변환
  const scaledData = (data || []).map(d => {
    // region_name이 없는 경우를 대비한 처리
    const regionName = d.region_name || d.region || '기타';
    return {
      ...d,
      region_name: regionName,
      total_sales_million: scaleToMillions(d.total_sales || 0)
    };
  });

  // 디버깅: 데이터 확인
  console.log('RegionalChart 데이터:', data);
  console.log('RegionalChart scaledData:', scaledData);

  return (
    <div className={`bg-white p-4 rounded-lg shadow relative ${className}`}>
      <h3 className="text-lg font-semibold mb-4">지역별 계약 현황</h3>
      <UnitBadge />
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={scaledData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="region_name" 
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tickFormatter={formatYAxis} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend />
          <Bar dataKey="contract_count" fill="#8B5CF6" name="계약 건수" />
          <Bar dataKey="total_sales_million" fill="#F59E0B" name="총 매출(백만원)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// 비용 항목별 파이 차트
export const CostBreakdownChart = ({ data, className = "" }) => {
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  return (
    <div className={`bg-white p-4 rounded-lg shadow ${className}`}>
      <h3 className="text-lg font-semibold mb-4">비용 항목별 분석</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="total_amount"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [`${value.toLocaleString()}원`, '금액']}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// 매출 항목(추가비용/관리비/민원) 막대 차트
export const RevenueItemChart = ({ data = [], className = "" }) => {
  const scaleToMillions = (v) => Math.round((v || 0) / 1_000_000);
  const formatYAxis = (v) => `${v.toLocaleString()}`;
  const tooltipFormatter = (value) => [`${(value || 0).toLocaleString()} 백만원`, '금액'];
  const scaledData = (data || []).map(d => ({ ...d, amount_million: scaleToMillions(d.amount) }));

  return (
    <div className={`bg-white p-4 rounded-lg shadow relative ${className}`}>
      <h3 className="text-lg font-semibold mb-4">항목 별 매출 통계</h3>
      <UnitBadge />
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={scaledData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="item" />
          <YAxis tickFormatter={formatYAxis} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend />
          <Bar dataKey="amount_million" fill="#06B6D4" name="금액(백만원)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// 계약 유형별 통계 차트
export const ContractTypeSalesChart = ({ data, className = "" }) => {
  const formatYAxis = (v) => `${v.toLocaleString()}`;
  const tooltipFormatter = (value) => [`${(value || 0).toLocaleString()}건`, '계약 건수'];

  const chartData = [
    {
      name: '신규 계약',
      계약건수: data?.new_contract?.contract_count || 0
    },
    {
      name: '안치자 추가 계약',
      계약건수: data?.additional_burial?.contract_count || 0
    }
  ];

  return (
    <div className={`bg-white p-4 rounded-lg shadow relative ${className}`}>
      <h3 className="text-lg font-semibold mb-4">계약 유형별 통계</h3>
      <UnitBadge text="단위: 건" />
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis tickFormatter={formatYAxis} />
          <Tooltip 
            formatter={tooltipFormatter}
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
          />
          <Legend />
          <Bar dataKey="계약건수" fill="#3B82F6" name="계약 건수" />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-sm text-gray-600">신규 계약</div>
          <div className="text-lg font-semibold">
            {data?.new_contract?.contract_count || 0}건
          </div>
          {/* <div className="text-sm text-gray-500">
            매출: {formatCurrency(data?.new_contract?.total_sales || 0)}
          </div> */}
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-sm text-gray-600">안치자 추가 계약</div>
          <div className="text-lg font-semibold">
            {data?.additional_burial?.contract_count || 0}건
          </div>
          {/* <div className="text-sm text-gray-500">
            매출: {formatCurrency(data?.additional_burial?.total_sales || 0)}
          </div> */}
        </div>
      </div>
    </div>
  );
};

// 통계 요약 카드 컴포넌트
export const StatisticsCard = ({ title, value, unit = "", change = null, className = "" }) => {
  const formattedValue = (() => {
    if (typeof value !== 'number') return value;
    if (unit === '원') return Math.trunc(value).toLocaleString();
    return value.toLocaleString();
  })();

  return (
    <div className={`bg-white p-4 rounded-lg shadow ${className}`}>
      <h3 className="text-sm font-medium text-gray-500 mb-1">{title}</h3>
      <div className="flex items-end justify-between">
        <div className="text-2xl font-bold text-gray-900">
          {formattedValue}
          {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
        </div>
        {change !== null && (
          <div className={`text-sm font-medium ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-500'
            }`}>
            {change > 0 ? '+' : ''}{change}%
          </div>
        )}
      </div>
    </div>
  );
};

// 통계 대시보드 메인 컴포넌트
export const StatisticsDashboard = ({
  salesData,
  contractData,
  tombTypeData,
  regionalData,
  costData,
  revenueItemData,
  contractTypeData,
  summaryData,
  className = "",
  period = 'month'
}) => {
  const contractCardTitle = period === 'day' ? '조회일 계약' : '이번 달 계약';
  return (
    <div className={`statistics-dashboard ${className}`}>
      {/* 요약 카드들 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatisticsCard
          title="총 매출"
          value={summaryData?.total_sales || 0}
          unit="원"
          change={summaryData?.sales_change || null}
        />
        <StatisticsCard
          title="총 계약 건수"
          value={summaryData?.total_contracts || 0}
          unit="건"
          change={summaryData?.contract_change || null}
        />
        <StatisticsCard
          title="평균 매출"
          value={summaryData?.average_sales || 0}
          unit="원"
        />
        {/* <StatisticsCard
          title={contractCardTitle}
          value={summaryData?.monthly_contracts || 0}
          unit="건"
        /> */}
      </div>

      {/* 차트들 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesChart data={salesData} className="" period={period} />
        <ContractChart data={contractData} className="" period={period} />
        <TombTypeChart data={tombTypeData} />
        <RegionalChart data={regionalData} />
      </div>

      {/* 계약 유형별 매출 통계 (전체 너비) */}
      {contractTypeData && (
        <div className="mt-6">
          <ContractTypeSalesChart data={contractTypeData} />
        </div>
      )}

      {/* 비용 분석 차트 (전체 너비) */}
      {/* <div className="mt-6">
        <CostBreakdownChart data={costData} />
      </div> */}

      {/* 항목 별 매출 통계 (전체 너비) */}
      <div className="mt-6">
        <RevenueItemChart data={revenueItemData || []} />
      </div>
    </div>
  );
};

export default StatisticsDashboard; 